import React, { useCallback, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FormHandles } from '@unform/core'
import { Form } from '@unform/web'
import Switch from 'react-switch'
import Modal from 'react-modal'
import * as Yup from 'yup'
import { MdSecurity } from 'react-icons/md'
import { FaFemale } from 'react-icons/fa'
import {
  FiUser,
  FiArrowLeft,
  FiArrowRight,
  FiAlertCircle,
  FiX,
} from 'react-icons/fi'
import getValidationErrors from '../../utils/getValidationErrors'
import usePersistedState from '../../hooks/usePersistedState'
import validaCPF from '../../utils/validaCPF'
import { agenciasBB } from '../../utils/agenciasBB.js'
import InputSelect from '../../components/InputSelect'
import Header from '../../components/Header'
import Button from '../../components/Button'
import Input from '../../components/Input'
import { Container, Content, InfoBox } from './styles'
import { UserData, UserDetails, UserAccount } from '../../utils/interfaces'

const ConfirmOwnership: React.FC = () => {
  const [userData, setUserData] = usePersistedState<UserData>(
    'userData',
    {} as UserData,
  )
  const [userAccount, setUserAccount] = usePersistedState<UserAccount>(
    'userAccount',
    {} as UserAccount,
  )
  const [userDetails, setUserDetails] = usePersistedState<UserDetails>(
    'userDetails',
    {} as UserDetails,
  )
  const [PaymentType, setPaymentType] = useState('')
  const [authorizeTitularPayment, setAuthorizeTitularPayment] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [bank, setBank] = useState('')
  const history = useHistory()

  const formRef = useRef<FormHandles>(null)

  const toggleAuthorizeTitularPayment = useCallback(() => {
    setAuthorizeTitularPayment(!authorizeTitularPayment)
  }, [authorizeTitularPayment, setAuthorizeTitularPayment])

  const handleSubmit = useCallback(
    async data => {
      try {
        await setUserData({
          ...userData,
          name: data.name,
          cpf: data.cpf,
          parental: data.parental,
        })
        await setUserAccount({
          ...userAccount,
          paymentType: data.paymentType,
          banco: data.bank * 1,
          conta: data.account,
          agencia: data.agency * 1,
        })
        // await setUserDetails({
        //   ...userDetails,
        //   tributacao: data.regimeTributacao,
        // })

        formRef.current?.setErrors({})

        const schema = Yup.object().shape({
          cpf: Yup.string()
            .required('CPF obrigatório')
            .test(
              '',
              'CPF inválido',
              () =>
                validaCPF(data.cpf.replaceAll('.', '').replace('-', '')) ||
                userData.cpf === '',
            ),
          name: Yup.string()
            .required('Seu nome é obrigatório.')
            .min(3, 'Digite o nome completo')
            .matches(/\s/g, 'Digite o nome completo'),
          parental: Yup.string()
            .required('Nome da mãe é obrigatório.')
            .min(3, 'Digite o nome completo da mãe'),
          paymentType: Yup.string().required('Selecione uma opção'),
          bank: Yup.string().when('paymentType', {
            is: 'D',
            then: Yup.string().required('Selecione um banco'),
          }),
          agency: Yup.string()
            .max(15)
            .when('paymentType', {
              is: 'D',
              then: Yup.string().required('Informe uma agência'),
            }),
          account: Yup.string().when('paymentType', {
            is: 'D',
            then: Yup.string().required('Informe uma conta'),
          }),
          // regimeTributacao: Yup.string().required('Selecione uma opção'),
        })

        await schema.validate(data, { abortEarly: false })

        history.push('/responsavel')
      } catch (err) {
        formRef.current?.setErrors(
          getValidationErrors(err as Yup.ValidationError),
        )
      }
    },
    [history, setUserData, userData, setUserAccount, userAccount],
  )

  const handlepayment = useCallback(async option => {
    setPaymentType(option.value)
  }, [])

  const handleBank = useCallback(
    async option => {
      setBank(option.value)
    },
    [setBank],
  )

  const handleChangeValues = useCallback(() => {
    history.push('/result-simulation')
  }, [history])

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  return (
    <>
      <Header>
        <FiArrowLeft
          type="button"
          cursor="pointer"
          onClick={handleChangeValues}
        />
      </Header>

      <Container>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={{
            name: userData.name,
            cpf: userData.cpf,
            // admission:
            //   userData.admission === undefined
            //     ? ''
            //     : userData.admission.split('-').reverse().join('/'),
            parental: userData.parental,
            contribuicaoBasica: userDetails.contribuicaoBasica,
            paymentType: userAccount.paymentType,
          }}
        >
          <Content>
            <h3>
              Precisamos de mais <strong>alguns dados</strong> para dar
              sequência à sua adesão
            </h3>
            <Input
              placeholder="CPF"
              name="cpf"
              icon={MdSecurity}
              type="tel"
              mask="cpf"
            />
            <Input placeholder="Nome completo" name="name" icon={FiUser} />
            {/* <Input
              icon={FiCalendar}
              name="admission"
              mask="date"
              placeholder="Data de admissão"
            /> */}
            <Input
              name="parental"
              placeholder="Nome Completo da Mãe"
              icon={FaFemale}
            />
            <div>
              <InputSelect
                name="paymentType"
                options={[
                  { label: 'Boleto', value: 'B' },
                  { label: 'Débito em conta (BB e Sicoob)', value: 'D' },
                ]}
                placeholder="Forma de pagamento da contribuição"
                onChange={option => {
                  handlepayment(option)
                }}
              />
            </div>

            {PaymentType === 'D' ? (
              <div>
                <InputSelect
                  name="bank"
                  options={[
                    { label: 'Banco do Brasil', value: '1' },
                    { label: 'SICOOB', value: '756' },
                  ]}
                  placeholder="Banco"
                  onChange={option => {
                    handleBank(option)
                  }}
                />
                {bank !== '756' ? (
                  <>
                    <InputSelect
                      name="agency"
                      options={agenciasBB}
                      placeholder="Agência"
                    />
                  </>
                ) : (
                  <>
                    <InputSelect
                      name="agency"
                      options={[{ label: '4198-7', value: '41987' }]}
                      placeholder="Agência"
                    />
                  </>
                )}
                <Input
                  type="text"
                  placeholder="Conta"
                  name="account"
                  mask="conta"
                  maxLength={20}
                  icon={FiUser}
                />
              </div>
            ) : (
              <div />
            )}

            {/* <InfoBox>
              <div className="placeholder">
                <small>Regime de tributação</small>
                <FiAlertCircle onClick={() => handleOpenModal()} />
              </div>
              <InputSelect
                name="regimeTributacao"
                options={[
                  { label: 'Regressivo', value: 'R' },
                  { label: 'Progressivo', value: 'P' },
                ]}
                placeholder=""
              />
            </InfoBox> */}
            <div id="authorization">
              <Switch
                onChange={toggleAuthorizeTitularPayment}
                checked={authorizeTitularPayment}
                checkedIcon={false}
                uncheckedIcon={false}
                height={22}
                width={44}
                handleDiameter={18}
                offColor="#DEE3E1"
                offHandleColor="#fff"
                onHandleColor="#fff"
                onColor="#4CA585"
              />
              <span>
                Autorizo a cobrança mensal recorrente do valor simulado
              </span>
            </div>

            {/* <Modal
              isOpen={isModalOpen}
              onRequestClose={handleCloseModal}
              overlayClassName="react-modal-overlay"
              className="react-modal-content"
            >
              <FiX onClick={handleCloseModal} />
              <strong>Regime de tributação</strong>
              <div>
                <h3>Regime Progressivo</h3>
                <p>
                  A renda recebida do plano é tributada de acordo com as
                  alíquotas previstas, a mesma utilizada para a tributação dos
                  salários. No momento da Declaração Anual de Impost ode Renda o
                  contribuinte efetuará os devidos acertos.
                </p>
              </div>
              <div>
                <h3>Regime Regressivo</h3>
                <p>
                  O percentual de desconto do Imposto de Renda sobre a renda
                  diminui com o tempo de permanência no plano. A tributação é
                  definitiva. Não permite ajuste na Declaração Anual de IR.
                </p>
              </div>
              <small>
                OBS.: a tabela progressiva pode ser alterada para regressiva até
                o último dia útil do mês seguinte à adesão.
              </small>
            </Modal> */}
          </Content>
        </Form>

        <Button
          type="button"
          color="green"
          disabled={!authorizeTitularPayment}
          onClick={() => formRef.current?.submitForm()}
        >
          <span>Seguir para a adesão</span>
          <div>
            <FiArrowRight />
          </div>
        </Button>
      </Container>
    </>
  )
}

export default ConfirmOwnership
